import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider } from 'antd'
import { FullMeetingFragment, ObjectiveStatus, ObjectiveDto } from '../../../../../types'
import { ObjectiveList, ObjectiveListType } from '../../../okr/lists/ObjectivesList'
import { filterObjectivesByTypeStatusAndRefYear } from '../../../okr/util'
import PerText from '../../../../../components/custom_components/Text'
import PerSpacer from '../../../../../components/custom_components/Spacer'
import PerFlexRow from '../../../../../components/custom_components/FlexRow'
import PerButtonCircle from '../../../../../components/custom_components/ButtonCircle'
import useObjectiveWithModalInMeeting from '../../../../../graphql/objectives/useObjectiveWithModalInMeeting'
import { TargetsModal } from '../../../../../components/custom_components/TargetsModal/TargetsModal'
import { useTargetsModalStore } from '../../../../../components/custom_components/TargetsModal/TargetsModalStore'
import { TargetModalType } from '../../../../../components/custom_components/TargetsModal/types'
import shallow from 'zustand/shallow'
import { retrieveCurrencyConversionRate } from '../../../../../shared/currencyConversionUtil'
import { useCurrency } from '../../../../../context/currency-context'
import { isDefined } from '../../../../../types.helper'

interface Props {
  employeeId: string
  meeting: FullMeetingFragment
  objectives: ObjectiveDto[]
}

const EmployeeObjectives: FC<Props> = ({ meeting, objectives }) => {
  const [t] = useTranslation()
  const { handleObjectiveDuplicate } = useObjectiveWithModalInMeeting({
    meetingId: meeting.id,
  })

  const { currency: currentContractCurrency, currencyConversionData } = useCurrency()

  const [openTargetModal] = useTargetsModalStore((state) => [state.openTargetModal], shallow)
  const {
    targetsCalculationHelper: { pastTargetsReferenceYear },
  } = meeting

  const monetaryObjectives = filterObjectivesByTypeStatusAndRefYear(objectives, true, [ObjectiveStatus.Approved], pastTargetsReferenceYear)
  const developmentObjectives = filterObjectivesByTypeStatusAndRefYear(
    objectives,
    false,
    [ObjectiveStatus.Approved, ObjectiveStatus.MarkedAsDone],
    pastTargetsReferenceYear,
  )
  const draftObjectives = objectives.filter((objective) => objective.targetAmount === null && objective.status === ObjectiveStatus.Proposal)

  const onObjectiveDeletion = (objective?: ObjectiveDto): void => {
    if (!!objective) {
      openTargetModal(
        TargetModalType.deleteTarget,
        objective,
        isDefined(objective.targetAmount)
          ? retrieveCurrencyConversionRate(objective.currency!, currentContractCurrency, currencyConversionData)
          : undefined,
      )
    }
  }

  return (
    <>
      <PerFlexRow direction="column">
        {(!!monetaryObjectives.length || !!developmentObjectives.length) && (
          <>
            <PerText appearance="heading" color="primary" mb="m">
              {t('okr.instructions.employee.review')}
            </PerText>
            {!!monetaryObjectives.length && (
              <PerSpacer mb="m">
                <ObjectiveList
                  listType={ObjectiveListType.Monetary}
                  objectives={monetaryObjectives}
                  handleObjectiveClicked={(_, objective) =>
                    openTargetModal(
                      TargetModalType.viewTargetInMeeting,
                      objective,
                      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
                      retrieveCurrencyConversionRate(objective!.currency!, currentContractCurrency, currencyConversionData),
                    )
                  }
                  handleObjectiveDelete={(objective?: ObjectiveDto) => onObjectiveDeletion(objective)}
                  isManagerPreparation={false}
                  employeeView
                />
              </PerSpacer>
            )}
            {!!developmentObjectives.length && (
              <ObjectiveList
                listType={ObjectiveListType.Development}
                objectives={developmentObjectives}
                isManagerPreparation={false}
                handleObjectiveClicked={(_, objective) => openTargetModal(TargetModalType.viewTargetInMeeting, objective)}
                handleObjectiveDelete={(objective?: ObjectiveDto) => onObjectiveDeletion(objective)}
                employeeView
              />
            )}
            <Divider />
          </>
        )}

        <PerFlexRow justify="space-between" align="middle" mb="xs" mr="s">
          <PerText appearance="heading" color="primary">
            {t('okr.instructions.employee.suggest')}
          </PerText>

          <PerButtonCircle
            icon="add_stroke"
            color="success"
            variant="pastel"
            tooltip={t('appraisalMeeting.buttons.newGoals')}
            onClick={() => openTargetModal(TargetModalType.appraisalSuggestedTarget)}
            data-e2e-add-more-targets-button
          />
        </PerFlexRow>

        <PerSpacer mb="s">
          <ObjectiveList
            listType={ObjectiveListType.Suggestion}
            objectives={draftObjectives}
            isManagerPreparation={false}
            handleObjectiveClicked={(_, objective) => openTargetModal(TargetModalType.appraisalSuggestedTarget, objective)}
            handleObjectiveDelete={(objective?: ObjectiveDto) => onObjectiveDeletion(objective)}
            handleObjectiveDuplicate={handleObjectiveDuplicate}
            showTasks
            canDuplicate
          />
        </PerSpacer>
      </PerFlexRow>

      <TargetsModal />
    </>
  )
}

export { EmployeeObjectives }
